import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  Eye,
  ArrowLeft,
  RefreshCw,
  LogOut,
  Upload,
  Receipt,
  UserCircle,
  BarChart3,
  Eye as EyeIcon,
  Users as UsersIcon,
  Monitor,
  Smartphone,
  TabletSmartphone,
  Globe
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const fileInputRef = useRef(null);

  const authHeader = {
    auth: {
      username: credentials.username,
      password: credentials.password
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard`, authHeader);
      setDashboardData(response.data);
      setIsAuthenticated(true);
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, authHeader);
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`);
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to fetch inquiries");
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to fetch bookings");
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error("Failed to fetch quotes");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, authHeader);
      setClients(response.data);
    } catch (error) {
      toast.error("Failed to fetch clients");
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports`, authHeader);
      setReports(response.data);
    } catch (error) {
      toast.error("Failed to fetch reports");
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoices`, authHeader);
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, authHeader);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInquiries();
      fetchBookings();
      fetchQuotes();
      fetchClients();
      fetchReports();
      fetchInvoices();
      fetchAnalytics();
    }
  }, [isAuthenticated]);

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status?status=${status}`);
      toast.success("Status updated");
      fetchInquiries();
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status?status=${status}`);
      toast.success("Status updated");
      fetchBookings();
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, authHeader);
      toast.success("Inquiry deleted");
      fetchInquiries();
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeader);
      toast.success("Booking deleted");
      fetchBookings();
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      await axios.delete(`${API}/admin/reports/${id}`, authHeader);
      toast.success("Report deleted");
      fetchReports();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, authHeader);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const updateInvoiceStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/invoices/${id}/status?status=${status}`, {}, authHeader);
      toast.success("Invoice status updated");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to update invoice status");
    }
  };

  const handleUploadReport = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.post(`${API}/admin/reports/upload`, formData, {
        ...authHeader,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Report uploaded successfully");
      setShowUploadModal(false);
      fetchReports();
      e.target.reset();
    } catch (error) {
      toast.error("Failed to upload report");
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      client_id: formData.get('client_id'),
      description: formData.get('description'),
      amount: parseFloat(formData.get('amount')),
      due_date: formData.get('due_date'),
      booking_id: formData.get('booking_id') || ""
    };
    
    try {
      await axios.post(`${API}/admin/invoices`, data, authHeader);
      toast.success("Invoice created successfully");
      setShowInvoiceModal(false);
      fetchInvoices();
      e.target.reset();
    } catch (error) {
      toast.error("Failed to create invoice");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-blue-500/20 text-blue-400";
      case "pending": return "bg-yellow-500/20 text-yellow-400";
      case "confirmed": return "bg-green-500/20 text-green-400";
      case "completed": return "bg-emerald-500/20 text-emerald-400";
      case "cancelled": return "bg-red-500/20 text-red-400";
      case "contacted": return "bg-purple-500/20 text-purple-400";
      default: return "bg-neutral-500/20 text-neutral-400";
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
                alt="AIP Logo" 
                className="h-16 w-auto mx-auto mb-6"
              />
            </Link>
            <h1 className="text-2xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-neutral-400">Sign in to manage your business</p>
          </div>

          <form onSubmit={handleLogin} className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="w-full bg-neutral-950 border border-neutral-800 rounded-lg h-12 px-4 text-white focus:border-[#B8960B] outline-none"
                  placeholder="admin"
                  required
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Password</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="w-full bg-neutral-950 border border-neutral-800 rounded-lg h-12 px-4 text-white focus:border-[#B8960B] outline-none"
                  placeholder="••••••••"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="btn-primary rounded-full w-full py-6 mt-6"
              disabled={isLoading}
              data-testid="admin-login-btn"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="text-center mt-6">
            <Link to="/" className="text-neutral-500 hover:text-white text-sm">
              <ArrowLeft className="inline mr-2" size={14} />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-64 bg-neutral-900/50 border-r border-neutral-800 p-6">
        <Link to="/">
          <img 
            src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
            alt="AIP Logo" 
            className="h-12 w-auto mb-8"
          />
        </Link>

        <nav className="space-y-2">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "inquiries", label: "Inquiries", icon: Users },
            { id: "bookings", label: "Bookings", icon: Calendar },
            { id: "quotes", label: "Quotes", icon: FileText },
            { id: "clients", label: "Clients", icon: UserCircle },
            { id: "reports", label: "Reports", icon: Upload },
            { id: "invoices", label: "Invoices", icon: Receipt },
            { id: "analytics", label: "Analytics", icon: BarChart3 }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-[#B8960B]/20 text-[#B8960B]' 
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
              }`}
              data-testid={`admin-tab-${item.id}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <button
            onClick={() => setIsAuthenticated(false)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors"
          >
            <LogOut size={20} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold capitalize">{activeTab}</h1>
            <p className="text-neutral-400">Manage your {activeTab}</p>
          </div>
          <Button 
            variant="outline" 
            className="btn-secondary"
            onClick={() => {
              fetchDashboardData();
              fetchInquiries();
              fetchBookings();
              fetchQuotes();
              fetchClients();
              fetchReports();
              fetchInvoices();
              fetchAnalytics();
            }}
          >
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && dashboardData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Users className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboardData.stats.inquiries.total}</div>
                    <div className="text-neutral-500 text-sm">Total Inquiries</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-blue-400">
                  {dashboardData.stats.inquiries.new} new
                </div>
              </div>

              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Calendar className="text-green-400" size={24} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboardData.stats.bookings.total}</div>
                    <div className="text-neutral-500 text-sm">Total Bookings</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-green-400">
                  {dashboardData.stats.bookings.confirmed} confirmed
                </div>
              </div>

              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Clock className="text-yellow-400" size={24} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboardData.stats.bookings.pending}</div>
                    <div className="text-neutral-500 text-sm">Pending Bookings</div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#B8960B]/20 flex items-center justify-center">
                    <FileText className="text-[#B8960B]" size={24} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboardData.stats.quotes.total}</div>
                    <div className="text-neutral-500 text-sm">Quotes Generated</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                <h3 className="font-semibold mb-4">Recent Inquiries</h3>
                <div className="space-y-3">
                  {dashboardData.recent_inquiries.map((inquiry, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg">
                      <div>
                        <div className="font-medium">{inquiry.name}</div>
                        <div className="text-sm text-neutral-500">{inquiry.email}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </div>
                  ))}
                  {dashboardData.recent_inquiries.length === 0 && (
                    <p className="text-neutral-500 text-center py-4">No inquiries yet</p>
                  )}
                </div>
              </div>

              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                <h3 className="font-semibold mb-4">Recent Bookings</h3>
                <div className="space-y-3">
                  {dashboardData.recent_bookings.map((booking, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg">
                      <div>
                        <div className="font-medium">{booking.name}</div>
                        <div className="text-sm text-neutral-500">{booking.booking_date} at {booking.booking_time}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                  ))}
                  {dashboardData.recent_bookings.length === 0 && (
                    <p className="text-neutral-500 text-center py-4">No bookings yet</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm text-neutral-400">Name</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Contact</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Service</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Status</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Date</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((inquiry) => (
                    <tr key={inquiry.id} className="border-t border-neutral-800">
                      <td className="p-4">
                        <div className="font-medium">{inquiry.name}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">{inquiry.email}</div>
                        <div className="text-sm text-neutral-500">{inquiry.phone}</div>
                      </td>
                      <td className="p-4 text-sm capitalize">{inquiry.service || "-"}</td>
                      <td className="p-4">
                        <select
                          value={inquiry.status}
                          onChange={(e) => updateInquiryStatus(inquiry.id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs bg-transparent border-0 cursor-pointer ${getStatusColor(inquiry.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="completed">Completed</option>
                        </select>
                      </td>
                      <td className="p-4 text-sm text-neutral-500">
                        {new Date(inquiry.timestamp).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => deleteInquiry(inquiry.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {inquiries.length === 0 && (
                <div className="text-center py-12 text-neutral-500">No inquiries yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm text-neutral-400">Client</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Property</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Date & Time</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Services</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Price</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Status</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="border-t border-neutral-800">
                      <td className="p-4">
                        <div className="font-medium">{booking.name}</div>
                        <div className="text-sm text-neutral-500">{booking.email}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">{booking.property_address}</div>
                        <div className="text-sm text-neutral-500">{booking.postcode}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">{booking.booking_date}</div>
                        <div className="text-sm text-neutral-500">{booking.booking_time}</div>
                      </td>
                      <td className="p-4 text-sm">
                        {booking.services.length} service(s)
                      </td>
                      <td className="p-4 text-sm text-[#B8960B] font-medium">
                        {booking.quoted_price > 0 ? `£${booking.quoted_price.toFixed(2)}` : "-"}
                      </td>
                      <td className="p-4">
                        <select
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs bg-transparent border-0 cursor-pointer ${getStatusColor(booking.status)}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => deleteBooking(booking.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <div className="text-center py-12 text-neutral-500">No bookings yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm text-neutral-400">Contact</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Property Type</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Furnished</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Services</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Total</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id} className="border-t border-neutral-800">
                      <td className="p-4">
                        <div className="font-medium">{quote.name || "Anonymous"}</div>
                        <div className="text-sm text-neutral-500">{quote.email || "-"}</div>
                      </td>
                      <td className="p-4 text-sm capitalize">{quote.property_type}</td>
                      <td className="p-4 text-sm capitalize">{quote.furnished}</td>
                      <td className="p-4 text-sm">{quote.services.length} service(s)</td>
                      <td className="p-4 text-sm text-[#B8960B] font-medium">
                        £{quote.total_price.toFixed(2)}
                      </td>
                      <td className="p-4 text-sm text-neutral-500">
                        {new Date(quote.timestamp).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {quotes.length === 0 && (
                <div className="text-center py-12 text-neutral-500">No quotes generated yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Clients Tab */}
        {activeTab === "clients" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm text-neutral-400">Name</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Email</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Phone</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id} className="border-t border-neutral-800">
                      <td className="p-4 font-medium">{client.name}</td>
                      <td className="p-4 text-sm">{client.email}</td>
                      <td className="p-4 text-sm text-neutral-500">{client.phone || "-"}</td>
                      <td className="p-4 text-sm text-neutral-500">
                        {new Date(client.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {clients.length === 0 && (
                <div className="text-center py-12 text-neutral-500">No registered clients yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex justify-end mb-6">
              <Button 
                className="btn-primary rounded-full"
                onClick={() => setShowUploadModal(true)}
              >
                <Upload size={16} className="mr-2" /> Upload Report
              </Button>
            </div>

            <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm text-neutral-400">Title</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Type</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Property</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Client</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Uploaded</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id} className="border-t border-neutral-800">
                      <td className="p-4 font-medium">{report.title}</td>
                      <td className="p-4 text-sm capitalize">{report.report_type}</td>
                      <td className="p-4 text-sm">{report.property_address}</td>
                      <td className="p-4 text-sm text-neutral-500">
                        {clients.find(c => c.id === report.client_id)?.name || "-"}
                      </td>
                      <td className="p-4 text-sm text-neutral-500">
                        {new Date(report.uploaded_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => deleteReport(report.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reports.length === 0 && (
                <div className="text-center py-12 text-neutral-500">No reports uploaded yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex justify-end mb-6">
              <Button 
                className="btn-primary rounded-full"
                onClick={() => setShowInvoiceModal(true)}
              >
                <Receipt size={16} className="mr-2" /> Create Invoice
              </Button>
            </div>

            <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm text-neutral-400">Invoice #</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Client</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Description</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Amount</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Due Date</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Status</th>
                    <th className="text-left p-4 text-sm text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-t border-neutral-800">
                      <td className="p-4 font-mono text-sm">{invoice.invoice_number}</td>
                      <td className="p-4 text-sm">
                        {clients.find(c => c.id === invoice.client_id)?.name || "-"}
                      </td>
                      <td className="p-4 text-sm">{invoice.description}</td>
                      <td className="p-4 text-sm text-[#B8960B] font-medium">£{invoice.amount?.toFixed(2)}</td>
                      <td className="p-4 text-sm text-neutral-500">{invoice.due_date}</td>
                      <td className="p-4">
                        <select
                          value={invoice.status}
                          onChange={(e) => updateInvoiceStatus(invoice.id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs bg-transparent border-0 cursor-pointer ${getStatusColor(invoice.status)}`}
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => deleteInvoice(invoice.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {invoices.length === 0 && (
                <div className="text-center py-12 text-neutral-500">No invoices created yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="analytics-tab"
          >
            {!analytics && (
              <div className="text-center py-12 text-neutral-500">Loading analytics...</div>
            )}

            {analytics && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: "Today", data: analytics.summary.today },
                    { label: "Last 7 days", data: analytics.summary.last_7_days },
                    { label: "Last 30 days", data: analytics.summary.last_30_days },
                    { label: "All time", data: analytics.summary.all_time },
                  ].map((card) => (
                    <div
                      key={card.label}
                      className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-5"
                      data-testid={`analytics-card-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">{card.label}</div>
                      <div className="flex items-end gap-6">
                        <div>
                          <div className="flex items-center gap-2 text-neutral-400 text-xs mb-1">
                            <EyeIcon size={12} /> Page views
                          </div>
                          <div className="text-2xl font-semibold text-white">{card.data.views.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-neutral-400 text-xs mb-1">
                            <UsersIcon size={12} /> Unique
                          </div>
                          <div className="text-2xl font-semibold text-[#B8960B]">{card.data.unique.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Daily Traffic Chart */}
                <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6 mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold">Daily traffic — last 30 days</h3>
                    <div className="flex items-center gap-4 text-xs text-neutral-500">
                      <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-sm bg-[#B8960B]"></span>Views</span>
                      <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-sm bg-neutral-600"></span>Unique</span>
                    </div>
                  </div>
                  {analytics.daily.length === 0 ? (
                    <div className="text-center py-8 text-neutral-500 text-sm">
                      No traffic recorded yet. Once visitors accept cookies, their visits will appear here.
                    </div>
                  ) : (
                    <div className="flex items-end gap-1 h-48">
                      {(() => {
                        const maxVal = Math.max(...analytics.daily.map(d => d.views), 1);
                        return analytics.daily.map((d) => (
                          <div key={d.date} className="flex-1 max-w-[40px] flex flex-col items-center justify-end group relative">
                            <div className="w-full flex flex-col items-center gap-0.5">
                              <div
                                className="w-full bg-[#B8960B] rounded-t-sm transition-opacity group-hover:opacity-80"
                                style={{ height: `${(d.views / maxVal) * 160}px`, minHeight: '2px' }}
                              />
                              <div
                                className="w-full bg-neutral-600 rounded-b-sm"
                                style={{ height: `${(d.unique / maxVal) * 160}px`, minHeight: '1px' }}
                              />
                            </div>
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden group-hover:block bg-neutral-800 text-xs text-white px-2 py-1 rounded whitespace-nowrap border border-neutral-700">
                              <div className="font-medium">{d.date}</div>
                              <div className="text-[#B8960B]">{d.views} views</div>
                              <div className="text-neutral-400">{d.unique} unique</div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  <div className="flex justify-between mt-2 text-xs text-neutral-600">
                    <span>{analytics.daily[0]?.date || ""}</span>
                    <span>{analytics.daily[analytics.daily.length - 1]?.date || ""}</span>
                  </div>
                </div>

                {/* Top pages, referrers, devices */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Top Pages */}
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">Top pages (30d)</h3>
                    {analytics.top_pages.length === 0 ? (
                      <div className="text-sm text-neutral-500">No data yet</div>
                    ) : (
                      <div className="space-y-3">
                        {analytics.top_pages.map((p) => {
                          const max = analytics.top_pages[0].views;
                          return (
                            <div key={p.path}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-neutral-300 truncate max-w-[200px]" title={p.path}>{p.path || "/"}</span>
                                <span className="text-[#B8960B] font-medium">{p.views}</span>
                              </div>
                              <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                                <div className="h-full bg-[#B8960B]" style={{ width: `${(p.views / max) * 100}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Top Referrers */}
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">Top referrers (30d)</h3>
                    {analytics.top_referrers.length === 0 ? (
                      <div className="text-sm text-neutral-500">No data yet</div>
                    ) : (
                      <div className="space-y-3">
                        {analytics.top_referrers.map((r) => {
                          const max = analytics.top_referrers[0].views;
                          return (
                            <div key={r.source}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-neutral-300 flex items-center gap-2">
                                  <Globe size={12} className="text-neutral-500" />
                                  {r.source}
                                </span>
                                <span className="text-[#B8960B] font-medium">{r.views}</span>
                              </div>
                              <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                                <div className="h-full bg-[#B8960B]" style={{ width: `${(r.views / max) * 100}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Devices */}
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">Devices (30d)</h3>
                    {analytics.devices.length === 0 ? (
                      <div className="text-sm text-neutral-500">No data yet</div>
                    ) : (
                      <div className="space-y-3">
                        {analytics.devices.map((d) => {
                          const total = analytics.devices.reduce((s, x) => s + x.views, 0);
                          const pct = total ? Math.round((d.views / total) * 100) : 0;
                          const Icon = d.device === "mobile" ? Smartphone : d.device === "tablet" ? TabletSmartphone : Monitor;
                          return (
                            <div key={d.device}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-neutral-300 flex items-center gap-2 capitalize">
                                  <Icon size={14} className="text-neutral-500" />
                                  {d.device}
                                </span>
                                <span className="text-[#B8960B] font-medium">{pct}%</span>
                              </div>
                              <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                                <div className="h-full bg-[#B8960B]" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* Upload Report Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 max-w-lg w-full"
          >
            <h2 className="text-xl font-bold mb-6">Upload Report</h2>
            <form onSubmit={handleUploadReport}>
              <div className="space-y-4">
                <div>
                  <Label className="text-neutral-400 text-sm">Client *</Label>
                  <select
                    name="client_id"
                    required
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg h-10 px-3 text-white mt-1"
                  >
                    <option value="">Select client...</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Report Title *</Label>
                  <Input name="title" required className="bg-neutral-950 border-neutral-800 mt-1" placeholder="e.g., Inventory Report - 123 Main St" />
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Report Type *</Label>
                  <select
                    name="report_type"
                    required
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg h-10 px-3 text-white mt-1"
                  >
                    <option value="inventory">Inventory Report</option>
                    <option value="check-in">Check-In Report</option>
                    <option value="check-out">Check-Out Report</option>
                    <option value="mid-term">Mid-Term Inspection</option>
                    <option value="snagging">Snagging Report</option>
                  </select>
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Property Address *</Label>
                  <Input name="property_address" required className="bg-neutral-950 border-neutral-800 mt-1" placeholder="Full property address" />
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">PDF File *</Label>
                  <input
                    type="file"
                    name="file"
                    accept=".pdf"
                    required
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3 text-white mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-[#B8960B] file:text-white"
                  />
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <Button type="button" variant="outline" className="btn-secondary flex-1" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1">
                  Upload Report
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 max-w-lg w-full"
          >
            <h2 className="text-xl font-bold mb-6">Create Invoice</h2>
            <form onSubmit={handleCreateInvoice}>
              <div className="space-y-4">
                <div>
                  <Label className="text-neutral-400 text-sm">Client *</Label>
                  <select
                    name="client_id"
                    required
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg h-10 px-3 text-white mt-1"
                  >
                    <option value="">Select client...</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Description *</Label>
                  <Input name="description" required className="bg-neutral-950 border-neutral-800 mt-1" placeholder="e.g., Inventory Report - 123 Main St" />
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Amount (£) *</Label>
                  <Input name="amount" type="number" step="0.01" required className="bg-neutral-950 border-neutral-800 mt-1" placeholder="95.00" />
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Due Date *</Label>
                  <Input name="due_date" type="date" required className="bg-neutral-950 border-neutral-800 mt-1" />
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <Button type="button" variant="outline" className="btn-secondary flex-1" onClick={() => setShowInvoiceModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1">
                  Create Invoice
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
